import { Action, Selector, State, StateContext } from '@ngxs/store';
import { inject, Injectable } from '@angular/core';
import { LegalTermsArchiveService } from '@pages/legal-terms-archive/services/legal-terms-archive.service';
import {
  DownloadArtifact,
  LoadLegalTermsArchive,
  LoadLegalTermsArchiveIfNotLoaded,
} from '@pages/legal-terms-archive/store/actions/legal-terms-archive.actions';
import { ArtifactCategory } from '@pages/legal-terms-archive/models/artifact-archive.interface';
import { DOCUMENT } from '@angular/common';
import { Observable, catchError, finalize, tap, throwError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';

interface LegalTermsArchiveStateModel {
  artifactCategories?: ArtifactCategory[];
  loading: boolean;
  artifactLoadingById: Record<string, boolean>;
  error: boolean;
}

@State<LegalTermsArchiveStateModel>({
  name: 'legalTermsArchive',
  defaults: {
    loading: false,
    artifactLoadingById: {},
    error: false,
  },
})
@Injectable()
export class LegalTermsArchiveState {
  private readonly legalTermsArchiveService = inject(LegalTermsArchiveService);
  private readonly document = inject(DOCUMENT);

  @Selector()
  static artifactCategories(state: LegalTermsArchiveStateModel): ArtifactCategory[] | undefined {
    return state.artifactCategories;
  }

  @Selector()
  static isLoading(state: LegalTermsArchiveStateModel): boolean {
    return state.loading;
  }

  @Selector()
  static isArtifactLoading(state: LegalTermsArchiveStateModel): (id: string) => boolean {
    return (id: string): boolean => state.artifactLoadingById[id];
  }

  @Selector()
  static isError(state: LegalTermsArchiveStateModel): boolean {
    return state.error;
  }

  @Action(LoadLegalTermsArchiveIfNotLoaded)
  loadLegalTermsArchiveIfNotLoaded(context: StateContext<LegalTermsArchiveStateModel>): void {
    const { artifactCategories } = context.getState();

    if (artifactCategories) {
      return;
    }

    context.dispatch(new LoadLegalTermsArchive());
  }

  @Action(LoadLegalTermsArchive)
  loadLegalTermsArchive(context: StateContext<LegalTermsArchiveStateModel>): Observable<ArtifactCategory[]> {
    context.patchState({ loading: true });

    return this.legalTermsArchiveService.getArtifacts().pipe(
      tap(artifactCategories => context.patchState({ artifactCategories })),
      catchError((httpErrorResponse: HttpErrorResponse) => {
        context.patchState({ error: true });

        return throwError(() => httpErrorResponse);
      }),
      finalize(() => context.patchState({ loading: false }))
    );
  }

  @Action(DownloadArtifact)
  downloadArtifact(context: StateContext<LegalTermsArchiveStateModel>, { id, filename }: DownloadArtifact): Observable<Blob> {
    const { artifactLoadingById } = context.getState();

    context.patchState({ artifactLoadingById: { ...artifactLoadingById, [id]: true } });

    return this.legalTermsArchiveService.getLegalTermsArtifactBlobById(id).pipe(
      tap(blob => this.downloadBlob(blob, filename)),
      finalize(() => {
        delete artifactLoadingById[id];
        context.patchState({ artifactLoadingById });
      })
    );
  }

  private downloadBlob(blob: Blob, filename: string): void {
    const url = URL.createObjectURL(blob);
    const a = this.document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    URL.revokeObjectURL(url);
  }
}
